import { parseMockRoutes } from '@config/runtime';
import { authHandlers } from '@mocks/routes/auth';
import { orgHandlers } from '@mocks/routes/orgs';
import { orgConsoleHandlers } from '@mocks/routes/org-console';
import { dashboardsHandlers } from '@mocks/routes/dashboards';
import { signalsHandlers } from '@mocks/routes/signals';
import { cloudConnectivityHandlers } from '@mocks/routes/cloudConnectivity';
import { metricsHandlers } from '@mocks/routes/metrics';
import { flowHandlers } from '@mocks/routes/flows';

export type MockRouteKey = 'auth' | 'orgs' | 'org-console' | 'dashboards' | 'signals' | 'metrics' | 'flows' | 'cloud-connectivity';

export interface MockContext {
  formData?: FormData | null;
  request: Request;
  url: URL;
}

function isFormDataBody(value: RequestInit['body'] | null | undefined): value is FormData {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Partial<FormData>;
  return (
    typeof candidate.append === 'function' &&
    typeof candidate.get === 'function' &&
    typeof candidate.entries === 'function'
  );
}

type MockHandler = (ctx: MockContext) => Promise<Response | null> | Response | null;

const registry: Record<MockRouteKey, MockHandler[]> = {
  auth: authHandlers,
  orgs: orgHandlers,
  'org-console': orgConsoleHandlers,
  dashboards: dashboardsHandlers,
  signals: signalsHandlers,
  metrics: metricsHandlers,
  flows: flowHandlers,
  'cloud-connectivity': cloudConnectivityHandlers,
};

const validRoutes = new Set<MockRouteKey>(['auth', 'orgs', 'org-console', 'dashboards', 'signals', 'metrics', 'flows', 'cloud-connectivity']);

export function shouldEnableMocks(mockRoutes: string[]): boolean {
  return normalizeMockRoutes(mockRoutes).length > 0;
}

export function createMockAwareFetch(mockRoutes: string[], baseFetch: typeof fetch): typeof fetch {
  const enabledRoutes = normalizeMockRoutes(mockRoutes);
  if (enabledRoutes.length === 0) {
    return baseFetch;
  }

  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const request = input instanceof Request ? input : new Request(input, init);
    const url = resolveRequestUrl(request);
    const formData = isFormDataBody(init?.body) ? init.body : null;

    for (const route of enabledRoutes) {
      const handlers = registry[route];
      for (const handler of handlers) {
        const result = await handler({ formData, request, url });
        if (result) {
          return result;
        }
      }
    }

    if (
      enabledRoutes.includes('dashboards') &&
      (url.pathname.includes('/dashboards/') || url.pathname.includes('/grafana/embed-session'))
    ) {
      console.warn('[mocks] dashboards route enabled but no handler matched', {
        method: request.method,
        path: url.pathname,
        search: url.search
      });
    }

    if (enabledRoutes.includes('signals') && url.pathname.includes('/signals/')) {
      console.warn('[mocks] signals route enabled but no handler matched', {
        method: request.method,
        path: url.pathname,
        search: url.search
      });
    }

    return baseFetch(request);
  };
}

function normalizeMockRoutes(mockRoutes: string[]): MockRouteKey[] {
  const routes = parseMockRoutes(mockRoutes.join(','))
    .filter((route): route is MockRouteKey => validRoutes.has(route as MockRouteKey));

  // Some route sets overlap (notably `org-console` contains broad /orgs/:orgId/* handlers).
  // Prefer more-specific handlers first so they can intercept before generic fallbacks.
  const priority: Record<MockRouteKey, number> = {
    auth: 0,
    orgs: 1,
    metrics: 2,
    dashboards: 3,
    signals: 4,
    flows: 5,
    'org-console': 6,
    'cloud-connectivity': 7
  };

  return routes.sort((a, b) => (priority[a] ?? 999) - (priority[b] ?? 999));
}

function resolveRequestUrl(request: Request): URL {
  try {
    return new URL(request.url);
  } catch (error) {
    return new URL(request.url, window.location.origin);
  }
}
